import { useMonitoringFile } from "@/context/MonitoringFileContext";
import { classNames } from "@/lib/utils";
import { Header } from "@components/SimpleTable/Headers";
import Button from "@components/library/Button";
import { ContextMenu } from "@components/library/ContextMenu";
import { FileLabelWithPopover } from "@components/library/Entities/FileLabelWithPopover";
import { ChevronDownIcon } from "@heroicons/react/outline";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { useEffect, useState } from "react";
import { useVersions } from "@/services/files.service";
import { usePointerEventsCleanup } from "@/lib/use-pointer-events-cleanup";

interface Props {
  fileId: string;
  versionId: string | null;
  onChange: (id: string | null) => void;
}

const VersionSelect = ({ fileId, versionId, onChange }: Props) => {
  const { versions } = useVersions(fileId);
  const selectedVersion = versions?.find((version) => version.version_id === versionId);

  useEffect(() => {
    if (versions && !selectedVersion && versionId !== null) {
      console.log(`Could not find selected config with ID ${versionId}, resetting to null.`);
      onChange(null);
    }
  }, [versions, onChange, selectedVersion, versionId]);

  const [open, setOpen] = useState(false);
  usePointerEventsCleanup(open);

  return (
    <DropdownMenu.Root open={open} onOpenChange={setOpen}>
      <DropdownMenu.Trigger asChild>
        <Button
          size={28}
          IconRight={ChevronDownIcon}
          className="w-[150px] whitespace-nowrap"
          title={selectedVersion ? selectedVersion.version_id : "Select Prompt Version"}
        >
          <div className="min-w-0 grow overflow-hidden text-ellipsis text-left">
            {selectedVersion ? (
              <FileLabelWithPopover
                file={selectedVersion}
                displayFile={false}
                size={20}
                tooltipContentProps={{
                  side: "right",
                }}
              />
            ) : (
              "Select Prompt Version"
            )}
          </div>
        </Button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <ContextMenu.Items
          isDropdownMenu
          align="end"
          sideOffset={4}
          className="z-[1010] max-h-[20rem] overflow-y-scroll font-normal"
        >
          <div className="px-12 py-8">
            <Header>Select Prompt Version</Header>
          </div>

          {versions?.map((version) => (
            <ContextMenu.Item
              key={version.version_id}
              withinDropdownMenu
              className={classNames(version.version_id === selectedVersion?.version_id && "bg-oldblue-200")}
              onClick={() => {
                onChange(version ? version.version_id : null);
              }}
            >
              <FileLabelWithPopover
                file={version}
                displayFile={false}
                size={20}
                tooltipContentProps={{
                  side: "right",
                }}
              />
            </ContextMenu.Item>
          ))}
        </ContextMenu.Items>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
};

export default VersionSelect;
